// skipcq: JS-W1028
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Link,
  Typography,
} from '@mui/material';
import useFirstTouchMetrics from '../../hooks/useFirstTouchMetrics';
import { capturePostHogEvent } from '../../utils/posthogClient';
import {
  readByHotspotMetrics,
  readFirstTouchTotals,
} from '../../utils/firstTouchReport';
import DashboardMetricPanel from './DashboardMetricPanel';
import FirstTouchHotspotsMap from './FirstTouchHotspotsMap';
import FirstTouchHotspotsTable from './FirstTouchHotspotsTable';

function FirstTouchHotspotsCard() {
  const { snapshot, loading, error } = useFirstTouchMetrics();
  const [tableVisible, setTableVisible] = useState(false);

  useEffect(() => {
    capturePostHogEvent('engage-view-first-touch-hotspots');
  }, []);

  const metrics = useMemo(() => snapshot?.metrics ?? {}, [snapshot]);
  const rows = useMemo(() => readByHotspotMetrics(metrics), [metrics]);
  const totals = useMemo(() => readFirstTouchTotals(metrics), [metrics]);
  const generatedAt =
    typeof metrics.generatedAt === 'string' ? metrics.generatedAt : null;

  const handleToggleTable = useCallback(() => {
    setTableVisible((previous) => !previous);
  }, []);

  const infoContent = (
    <>
      Where do users first interact with the app? Aggregated from immutable per-user
      first-activity records across logged entries and reviews, with brewery/event coordinates for the
      heat map.
      {snapshot ? (
        <>
          {generatedAt ? ` Snapshot generated ${generatedAt}.` : ''}
          {' '}
          {totals.usersWithFirstTouch} users across {totals.hotspotCount} hotspots.
          {totals.usersMissingCoordinates > 0
            ? ` ${totals.usersMissingCoordinates} users lack map coordinates (table only).`
            : ''}
        </>
      ) : null}
    </>
  );

  const panelContent = useMemo(() => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      );
    }

    if (error) {
      return <Alert severity="error">{error}</Alert>;
    }

    if (!snapshot) {
      return (
        <Typography variant="body2" color="text.secondary">
          No first-touch hotspot metrics yet. Run the backend reporter or backfill script.
        </Typography>
      );
    }

    return (
      <>
        <Typography variant="body2" color="text.secondary" paragraph>
          {totals.usersWithFirstTouch} users across {totals.hotspotCount} hotspots.
          {totals.usersMissingCoordinates > 0
            ? ` ${totals.usersMissingCoordinates} users lack map coordinates (table only).`
            : ''}
        </Typography>

        <FirstTouchHotspotsMap rows={rows} />

        <Link
          component="button"
          variant="body2"
          onClick={handleToggleTable}
          aria-expanded={tableVisible}
          sx={{ mt: 2, display: 'inline-block' }}
        >
          {tableVisible ? 'Hide detail table' : 'Show detail table'}
        </Link>

        {tableVisible ? (
          <Box sx={{ mt: 2 }}>
            <FirstTouchHotspotsTable rows={rows} />
          </Box>
        ) : null}
      </>
    );
  }, [
    error,
    handleToggleTable,
    loading,
    rows,
    snapshot,
    tableVisible,
    totals.hotspotCount,
    totals.usersMissingCoordinates,
    totals.usersWithFirstTouch,
  ]);

  return (
    <DashboardMetricPanel title="First-touch hotspots" infoContent={infoContent}>
      {panelContent}
    </DashboardMetricPanel>
  );
}

export default FirstTouchHotspotsCard;
